import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { Sheet } from './Sheet';

/**
 * Un filtro = un'etichetta con il valore corrente, che apre un foglio dal basso.
 *
 * **Perché non un `<select>` nativo**, che costava molto meno: il menu di
 * sistema arriva col suo grigio e le sue forme, dentro un'app interamente verde
 * acqua. Su una schermata è un dettaglio; su quattro filtri diventa la cosa che
 * si nota.
 *
 * Quello che si perde: il selettore di sistema è gratuito, accessibile e
 * familiare. Ricostruendolo tocca a noi la chiusura col tasto indietro, il
 * blocco dello scorrimento dietro e il segno sulla voce scelta — tutte cose che
 * ora vivono in `Sheet`, condiviso con gli altri pannelli dell'app.
 */
export function SelectFilter<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  className?: string;
}) {
  const haptics = useHaptics();
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value)?.label ?? value;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          haptics.impact('light');
          setOpen(true);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'card-edge flex min-w-0 flex-1 items-center gap-1.5 rounded-full bg-bg-secondary px-3.5 py-2',
          'transition-opacity active:opacity-80',
          className,
        )}
      >
        {/* L'etichetta dice COSA si sta filtrando anche quando il valore è
            «Tutti» — cioè proprio quando il filtro è inattivo e senza etichetta
            sembrerebbe un bottone qualunque. */}
        <span className="shrink-0 text-xs font-bold text-fg">{label}:</span>
        <span className="min-w-0 flex-1 truncate text-left text-xs font-bold text-fg">
          {current}
        </span>
        <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-fg-muted" strokeWidth={2.5} />
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} label={label}>
        {/* Il titolo resta fermo mentre l'elenco scorre: con molte voci si
            perde altrimenti di vista quale filtro si sta scegliendo. */}
        <div className="sticky top-0 z-10 bg-bg-secondary px-4 pb-2 pt-2">
          <p className="text-xs font-bold uppercase tracking-wide text-fg-muted">{label}</p>
        </div>

        <ul role="listbox" className="px-2 pb-2">
          {options.map((o) => {
            const on = o.value === value;
            return (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => {
                    haptics.selection();
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-sm font-semibold',
                    'transition-colors active:opacity-80',
                    on ? 'bg-brand/15 text-brand' : 'text-fg',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {/* Il segno di spunta e non solo il colore: su una scelta già
                      filtrata si cerca «quale ho preso», e un colore da solo
                      costringe a confrontare le righe fra loro. */}
                  {on && <Check aria-hidden className="h-4 w-4 shrink-0" strokeWidth={3} />}
                </button>
              </li>
            );
          })}
        </ul>
      </Sheet>
    </>
  );
}
